import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronRight, CheckCircle, XCircle, HelpCircle, Search } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const PAGE_SIZE = 20;

const PartnerBotsPage = () => {
    const [bots, setBots] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ownerDraft, setOwnerDraft] = useState('');
    const [botDraft, setBotDraft] = useState('');
    const [appliedOwnerSearch, setAppliedOwnerSearch] = useState('');
    const [appliedBotSearch, setAppliedBotSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [runnerStatus, setRunnerStatus] = useState({ running: [], failed: [], updated_at: null });
    const navigate = useNavigate();

    const loadBots = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: PAGE_SIZE.toString(),
            });
            if (filterActive !== 'all') params.set('active_only', filterActive === 'active' ? 'true' : 'false');
            const os = appliedOwnerSearch.trim();
            const bs = appliedBotSearch.trim();
            if (os) params.set('owner_search', os);
            if (bs) params.set('bot_search', bs.replace(/^@/, ''));
            const [botsRes, statusRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/referrals/bots?${params.toString()}`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }),
                fetch(`${API_BASE_URL}/api/referrals/bots/runner-status`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }),
            ]);
            if (!botsRes.ok) throw new Error('Не удалось загрузить список ботов');
            const data = await botsRes.json();
            setBots(data.bots || []);
            setTotal(data.total ?? 0);
            if (statusRes.ok) {
                const status = await statusRes.json();
                setRunnerStatus(status);
            }
        } catch (e) {
            setError(e.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    const getBotStatus = (botId) => {
        if (runnerStatus.running?.includes(botId)) return { label: 'Работает', icon: CheckCircle, color: 'text-green-600' };
        const failed = runnerStatus.failed?.find((f) => f.id === botId);
        if (failed) return { label: `Ошибка (код ${failed.exit_code})`, icon: XCircle, color: 'text-rose-600' };
        return { label: '—', icon: HelpCircle, color: 'text-slate-400' };
    };

    const runSearch = () => {
        setAppliedOwnerSearch(ownerDraft.trim());
        setAppliedBotSearch(botDraft.trim());
        setPage(1);
    };

    const onSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            runSearch();
        }
    };

    useEffect(() => { loadBots(); }, [page, filterActive, appliedOwnerSearch, appliedBotSearch]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedBots = [...bots].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        const numA = Number(a[sortConfig.key] ?? 0);
        const numB = Number(b[sortConfig.key] ?? 0);
        if (numA < numB) return -1 * dir;
        if (numA > numB) return 1 * dir;
        return 0;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Партнёрские боты</h2>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1">
                        <Search size={16} className="text-slate-400 shrink-0" />
                        <input
                            type="search"
                            value={ownerDraft}
                            onChange={(e) => setOwnerDraft(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                            placeholder="Владелец (@username, ФИО)"
                            className="min-w-[10rem] max-w-[14rem] text-sm py-1 px-1 border-0 focus:ring-0 outline-none bg-transparent"
                        />
                    </div>
                    <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1">
                        <Search size={16} className="text-slate-400 shrink-0" />
                        <input
                            type="search"
                            value={botDraft}
                            onChange={(e) => setBotDraft(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                            placeholder="Бот (@username)"
                            className="min-w-[10rem] max-w-[14rem] text-sm py-1 px-1 border-0 focus:ring-0 outline-none bg-transparent"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={runSearch}
                        disabled={loading}
                        className="flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-800 px-3 py-2 text-sm font-medium hover:bg-indigo-100 transition disabled:opacity-50"
                    >
                        <Search size={16} />
                        Поиск
                    </button>
                    <select
                        value={filterActive}
                        onChange={(e) => { setFilterActive(e.target.value); setPage(1); }}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                        <option value="all">Все</option>
                        <option value="active">Только активные</option>
                    </select>
                    <button
                        onClick={loadBots}
                        disabled={loading}
                        className="flex items-center space-x-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        <span>Обновить</span>
                    </button>
                </div>
            </div>

            {runnerStatus.updated_at && (
                <div className="text-sm text-slate-500">
                    Статус runner: обновлён {runnerStatus.updated_at} · Работают: {runnerStatus.running?.length ?? 0} · С ошибками: {runnerStatus.failed?.length ?? 0}
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw className="animate-spin text-slate-400" size={32} />
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Статус</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">API</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Бот</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Владелец</th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('margin_percent')}
                                    >
                                        Наценка
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('orders_count')}
                                    >
                                        Заказы
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('users_total')}
                                    >
                                        Пользователи
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('total_revenue')}
                                    >
                                        Выручка
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('bot_balance_rub')}
                                    >
                                        Баланс
                                    </th>
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('star_price_sbp')}
                                    >
                                        Цена ⭐
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                                        База
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {sortedBots.map((bot) => {
                                    const status = getBotStatus(bot.id);
                                    const StatusIcon = status.icon;
                                    return (
                                    <tr
                                        key={bot.id}
                                        className="hover:bg-slate-50 cursor-pointer"
                                        onClick={() => navigate(`/partner-bots/${bot.id}`)}
                                    >
                                        <td className="px-6 py-4 text-sm">
                                            <span className={`inline-flex items-center gap-1 ${status.color}`} title={status.label}>
                                                <StatusIcon size={18} />
                                                {status.label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {bot.is_active ? (
                                                <span className="text-green-700">Вкл</span>
                                            ) : (
                                                <span className="text-amber-700">Выкл</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <span className="font-mono text-indigo-600">@{bot.bot_username || '—'}</span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            @{bot.owner_username || bot.owner_user_id}
                                        </td>
                                        <td className="px-6 py-4 text-sm">{bot.margin_percent}%</td>
                                        <td className="px-6 py-4 text-sm">{bot.orders_count ?? 0}</td>
                                        <td className="px-6 py-4 text-sm">{bot.users_total ?? 0}</td>
                                        <td className="px-6 py-4 text-sm font-semibold text-green-600">
                                            {parseFloat(bot.total_revenue || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                                        </td>
                                        <td className="px-6 py-4 text-sm font-semibold text-slate-700">
                                            {parseFloat(bot.bot_balance_rub || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                                        </td>
                                        <td className="px-6 py-4 text-sm">{parseFloat(bot.star_price_sbp || 0).toFixed(2)} ₽</td>
                                        <td className="px-6 py-4 text-sm text-slate-500">{parseFloat(bot.referral_base_price || 0).toFixed(2)} ₽</td>
                                        <td className="px-6 py-4">
                                            <ChevronRight className="text-slate-400" size={18} />
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {bots.length === 0 && !loading && (
                        <div className="p-12 text-center text-slate-500">Партнёрских ботов пока нет.</div>
                    )}
                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
                            <span className="text-sm text-slate-500">Всего: {total}</span>
                            <div className="flex gap-2">
                                <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-50">Назад</button>
                                <span className="px-3 py-1 text-sm">Стр. {page}</span>
                                <button disabled={page * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-50">Вперёд</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PartnerBotsPage;
